import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../../Utils/persistHelpers"
import { logout } from "../../../LoginPage/redux/auth.actions"
import { CE_PAGE_STATE } from "../constants/page-states"
import { RECEIPT_COMPELLING_EVIDENCE_SLICE_NAME as SLICE_NAME } from "./compelling-evidence.constants"
import {
  getCompellingEvidenceCase,
  getCompellingEvidenceCases,
  getCompellingEvidenceExport,
  getCompellingEvidenceKpi,
  getCompellingEvidenceState
} from "./compelling-evidence.thunks"

export const CASE_COLUMNS = [
  { field_id: "date", label: "Date", active: true },
  { field_id: "card", label: "Card", active: true },
  { field_id: "amount", label: "Amount", active: true },
  { field_id: "program", label: "Program", active: true },
  { field_id: "outcome", label: "Outcome", active: true },
  { field_id: "fee_charged", label: "Fee charged", active: true }
]

const initialFilters = {
  date: null,
  card: [],
  amount: { min: null, max: null, type: null },
  program: "all",
  outcome: "all",
  fee_charged: { min: null, max: null, type: null }
}

const initialKpi = {
  disputesStopped: 0,
  stoppedRate: 0,
  revenueProtected: 0,
  feesCharged: 0
}

const initialResult = {
  count: 0,
  results: []
}

const initialCaseDetails = {
  data: null,
  fetching: false
}

const initialState = {
  pageState: null, // check CE_PAGE_STATE
  hasIntegration: false,
  programs: [],
  stateFetching: false,

  selectedProgram: "all",

  kpi: initialKpi,
  kpiFetching: false,

  result: initialResult,
  fetching: false,
  exportFetching: false,

  caseDetails: initialCaseDetails,

  filters: initialFilters,
  visibleFilters: {
    date: true,
    card: true,
    amount: true,
    program: true,
    outcome: true,
    fee_charged: true
  },
  columns: CASE_COLUMNS,
  viewMode: "table"
}

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setProp(state, { payload: { key, value } }) {
      if (["filters", "visibleFilters"].includes(key)) {
        state[key] = { ...state[key], ...value }
      } else {
        state[key] = value
      }
    },
    setSelectedProgram(state, { payload }) {
      state.selectedProgram = payload || "all"
    },
    clearFilters(state) {
      state.filters = initialFilters
    }
  },
  extraReducers: (builder) => {
    builder
      // The page aborts a request it no longer needs (business account, program or filters
      // switched); that rejection must not clear what the request replacing it is about to load.
      .addCase(getCompellingEvidenceState.pending, (state) => {
        state.stateFetching = true
      })
      .addCase(getCompellingEvidenceState.fulfilled, (state, { payload }) => {
        state.stateFetching = false
        state.programs = payload?.programs ?? []
        state.pageState = payload?.page_state ?? null
        state.hasIntegration = payload?.has_integration ?? false
        // Behind an overlay the KPI cards read zero, not another account's or an earlier
        // enrollment's figures.
        if (state.pageState !== CE_PAGE_STATE.ACTIVE) {
          state.kpi = initialKpi
          state.result = initialResult
        }
      })
      .addCase(getCompellingEvidenceState.rejected, (state, { meta }) => {
        if (meta.aborted) return
        state.stateFetching = false
        state.programs = []
        state.pageState = null
      })

      .addCase(getCompellingEvidenceKpi.pending, (state) => {
        state.kpiFetching = true
      })
      .addCase(getCompellingEvidenceKpi.fulfilled, (state, { payload }) => {
        state.kpiFetching = false
        state.kpi = {
          disputesStopped: Number(payload?.disputes_stopped ?? 0),
          stoppedRate: Number(payload?.stopped_rate ?? 0),
          revenueProtected: Number(payload?.revenue_protected ?? 0),
          feesCharged: Number(payload?.fees_charged ?? 0)
        }
      })
      .addCase(getCompellingEvidenceKpi.rejected, (state, { meta }) => {
        if (meta.aborted) return
        state.kpiFetching = false
        state.kpi = initialKpi
      })

      .addCase(getCompellingEvidenceCases.pending, (state) => {
        state.fetching = true
      })
      .addCase(getCompellingEvidenceCases.fulfilled, (state, { payload }) => {
        state.fetching = false
        state.result = {
          count: payload?.count ?? payload?.results?.length ?? 0,
          results: payload?.results ?? []
        }
      })
      .addCase(getCompellingEvidenceCases.rejected, (state, { meta }) => {
        if (meta.aborted) return
        state.fetching = false
        state.result = initialResult
      })

      // Cleared on pending, so the drawer never shows the previous case under the new one's header.
      .addCase(getCompellingEvidenceCase.pending, (state) => {
        state.caseDetails = { data: null, fetching: true }
      })
      .addCase(getCompellingEvidenceCase.fulfilled, (state, { payload }) => {
        state.caseDetails = { data: payload, fetching: false }
      })
      .addCase(getCompellingEvidenceCase.rejected, (state, { meta }) => {
        if (meta.aborted) return
        state.caseDetails = initialCaseDetails
      })

      .addCase(getCompellingEvidenceExport.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getCompellingEvidenceExport.fulfilled, (state) => {
        state.exportFetching = false
      })
      .addCase(getCompellingEvidenceExport.rejected, (state) => {
        state.exportFetching = false
      })

      .addCase(logout, (state) => ({
        ...initialState,
        columns: state.columns,
        visibleFilters: state.visibleFilters,
        viewMode: state.viewMode
      }))
  }
})

export const { setProp, setSelectedProgram, clearFilters } = slice.actions

/* ------------- Selectors ------------- */
export const selectCompellingEvidence = (state) => state.receiptCompellingEvidence
export const selectPrograms = (state) => state.receiptCompellingEvidence.programs
export const selectStateFetching = (state) => state.receiptCompellingEvidence.stateFetching
export const selectKpi = (state) => state.receiptCompellingEvidence.kpi
export const selectExportFetching = (state) => state.receiptCompellingEvidence.exportFetching
export const selectCaseDetails = (state) => state.receiptCompellingEvidence.caseDetails
export const selectKpiFetching = (state) => state.receiptCompellingEvidence.kpiFetching
export const selectAreFiltersDefault = (state) =>
  isEqual(initialFilters, state.receiptCompellingEvidence.filters)

/* ------------- Persistence ------------- */
const PERSISTED_REDUCER_VERSION = 1

export const persistedReducer = persistReducer(
  {
    key: SLICE_NAME,
    storage,
    whitelist: ["columns", "visibleFilters", "viewMode"],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return { ...state, ...initialState }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
